import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosInstance';

const ProfilePage = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/profile/${userId}`);
      setProfile(response.data.profile);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Laden des Profils');
    } finally {
      setLoading(false);
    }
  };

  const getFullAvatarUrl = (avatarUrl) => {
    if (!avatarUrl) return '/uploads/avatars/default.png';
    if (avatarUrl.startsWith('http')) return avatarUrl;
    return `https://forumapi.more-features.com${avatarUrl}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getGroupColor = (group) => {
    return group.color || '#6B7280';
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen  p-4">
        <div className="max-w-4xl mx-auto">
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen  p-4">
        <div className="max-w-4xl mx-auto">
          <div className="alert alert-warning">
            <span>Profil nicht gefunden</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  p-4">
      <div className="max-w-4xl mx-auto">
        {/* Profile Header */}
        <div className="card bg-base-100 shadow-md mb-6">
          <div className="card-body">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0 self-center sm:self-start">
                <div className="avatar">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
                    <img
                      src={getFullAvatarUrl(profile.avatarUrl)}
                      alt={profile.username}
                      className="object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Basic Info */}
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h1 className="text-2xl sm:text-3xl font-bold text-base-content truncate">
                    {profile.username}
                  </h1>
                  
                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
                    {profile.canSendPM && (
                      <Link
                        to={`/pm?compose=true&to=${profile.username}`}
                        className="bg-[#448f65] text-white px-3 py-2 rounded-lg hover:bg-[#2d5f43] transition-colors hover:text-white text-sm text-center"
                      >
                        PM senden
                      </Link>
                    )}
                    {currentUser?.id === userId && (
                      <Link
                        to="/usercp/profile"
                        className="btn btn-outline btn-sm"
                      >
                        Bearbeiten
                      </Link>
                    )}
                  </div>
                </div>

              {/* User Groups */}
              {profile.profileSettings?.showGroups !== false && profile.userGroups?.length > 0 && (
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                    {profile.userGroups.map(group => (
                      <span
                        key={group._id}
                        className="px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-medium text-white"
                        style={{ backgroundColor: getGroupColor(group) }}
                      >
                        {group.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Registration Date */}
              {profile.profileSettings?.showRegistrationDate !== false && (
                <div className="mt-3 text-base-content/70 text-center sm:text-left">
                  <span className="text-xs sm:text-sm">
                    Registriert seit: {formatDate(profile.registrationDate)}
                  </span>
                </div>
              )}

              {/* Warning Status */}
              {profile.warningStatus && (
                <div className="mt-3 flex justify-center sm:justify-start">
                  {profile.warningStatus.hasActiveWarnings ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.warningStatus.yellowCards > 0 && (
                        <span className="badge badge-warning text-xs">
                          {profile.warningStatus.yellowCards} Gelbe Karte(n)
                        </span>
                      )}
                      {profile.warningStatus.redCards > 0 && (
                        <span className="badge badge-error text-xs">
                          {profile.warningStatus.redCards} Rote Karte(n)
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="badge badge-success text-xs">Keine aktiven Verwarnungen</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
          {/* Extended Profile Fields */}
          {profile.profileType === 'extended' && profile.customFields?.length > 0 && (
            <div className="xl:col-span-1">
              <div className="card bg-base-100 shadow-md">
                <div className="card-body">
                  <h2 className="card-title text-lg">
                    Erweiterte Informationen
                  </h2>
                
                <div className="space-y-3">
                  {profile.customFields.map(cf => (
                    <div key={cf.field._id}>
                      <dt className="text-xs sm:text-sm font-medium text-base-content/60">
                        {cf.field.label}
                      </dt>
                      <dd className="mt-1 text-xs sm:text-sm text-base-content break-words">
                        {cf.field.type === 'url' ? (
                          <a 
                            href={cf.value} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="link link-primary break-all"
                          >
                            {cf.value}
                          </a>
                        ) : cf.field.type === 'email' ? (
                          <a 
                            href={`mailto:${cf.value}`}
                            className="link link-primary break-all"
                          >
                            {cf.value}
                          </a>
                        ) : cf.field.type === 'date' ? (
                          formatDate(cf.value)
                        ) : (
                          <span className="break-words">{cf.value || '-'}</span>
                        )}
                      </dd>
                    </div>
                  ))}
                </div>
                </div>
              </div>
            </div>
          )}

          {/* Recent Posts */}
          {profile.profileSettings?.showLastPosts !== false && profile.recentPosts?.length > 0 && (
            <div className={profile.profileType === 'extended' && profile.customFields?.length > 0 ? 'xl:col-span-2' : 'xl:col-span-3'}>
              <div className="card bg-base-100 shadow-md">
                <div className="card-body">
                  <h2 className="card-title text-lg">
                    Letzte Beiträge ({profile.recentPosts.length})
                  </h2>
                  
                  <div className="space-y-3 lg:space-y-4">
                    {profile.recentPosts.map(post => (
                      <div key={post._id} className="border-b border-base-300 pb-3 lg:pb-4 last:border-b-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/thread/${post.thread._id}#post-${post._id}`}
                              className="text-base-content hover:text-primary font-medium block truncate sm:whitespace-normal"
                            >
                              {post.thread.title}
                            </Link>
                            <p className="text-base-content/70 text-xs sm:text-sm mt-1 overflow-hidden"
                               style={{ 
                                 display: '-webkit-box',
                                 WebkitLineClamp: 2,
                                 WebkitBoxOrient: 'vertical'
                               }}>
                              {post.content}
                            </p>
                          </div>
                          <div className="text-xs text-base-content/50 sm:ml-4 flex-shrink-0">
                            {formatDate(post.createdAt)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No Content Message */}
          {(!profile.recentPosts || profile.recentPosts.length === 0) && 
           (profile.profileType !== 'extended' || !profile.customFields?.length) && (
            <div className="xl:col-span-3">
              <div className="card bg-base-100 shadow-md">
                <div className="card-body text-center">
                  <div className="text-base-content/60">
                    <p className="text-sm">Noch keine weiteren Informationen verfügbar</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
